import getCompanyProductApi from '@/api/get/company/company-product/get-company-product';
import getCompanyTemplateApi from '@/api/get/company/company-template/get-company-template';
import updateCompanyProductApi from '@/api/patch/company/company-product/update-company-product';
import Alert from '@/components/alert/alert-component';
import FormWrapper from '@/components/form-wrapper/form-wrapper-component';
import { useMutation } from '@/hooks/use-api';
import uploadToTempS3Base64 from '@/utils/upload-to-s3-base64';
import uploadToTempS3File from '@/utils/upload-to-s3-file';
import { removeUndefinedProperties } from '@/utils/utils';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ProductContextProvider } from './context-provider';
import ProductForm from './product-form';
import createFlatOptions, {
  convertTemplateToProduct,
  recursiveSetOptionOrder,
} from './utils';

/**
 * Edit product component.
 * @returns {JSX.Element} The rendered Product Add page component.
 */
const ProductEdit = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { id, productId } = useParams();
  const [prices, setPrices] = useState([]);
  const [options, setOptions] = useState([]);
  const [productData, setProductData] = useState({});
  const [templateData, setTemplateData] = useState({});

  const contextValue = {
    prices,
    options,
    setOptions,
    setPrices,
    templateData,
    uniqueContextId: Symbol('product-options-context'),
  };

  const [updateProductApi, { loading: editProductLoading }] = useMutation(
    updateCompanyProductApi,
  );

  const onUpdateProduct = async (
    values,
    shouldNavigate = true,
    onRefresh = () => {},
  ) => {
    try {
      // update product
      setLoading(true);

      const {
        categories: categoriesOriginal,
        productGallery: productGalleryOriginal,
        productDocuments: productDocumentsOriginal,
        productLinks: productLinksOriginal,
        productOptions: productOptionsOriginal,
      } = productData ?? {};

      const {
        product,
        categories,
        productGallery,
        productDocuments,
        productLinks,
        productOptions,
      } = values;

      delete product.id;
      delete product.created_at;
      delete product.updated_at;

      if (product?.thumbnail?.file) {
        const { file, fileName, fileType } = values.product.thumbnail;
        await uploadToTempS3Base64(
          file,
          `image-upload/${fileName}`,
          fileType,
        ).then((data) => {
          product.thumbnail = `${data.id}`;
        });
      }

      const addCategories = categories
        ?.filter(
          (c1) => !categoriesOriginal?.some((c2) => c1 === c2.category_id),
        )
        .map((a) => a); // adding by category id
      const removeCategories = categoriesOriginal
        ?.filter((c1) => !categories?.some((c2) => c1.category_id === c2))
        ?.map((c) => c.category_id);

      const removeGallery = productGalleryOriginal
        ?.filter((c1) => !productGallery?.some((c2) => c1.id === c2.id))
        ?.map((c) => c.id);

      const newGallery = productGallery
        ?.filter((c1) => !productGalleryOriginal?.some((c2) => c1.id === c2.id))
        ?.map(async (gallery, index) => {
          const { file } = gallery;
          if (file) {
            return uploadToTempS3File(file, `image-upload/${file.name}`).then(
              (data) => {
                const fileKey = data?.id;
                const updatedGallery = {
                  image: fileKey,
                  order: productGalleryOriginal.length + (index + 1),
                };
                return updatedGallery;
              },
            );
          }
          return gallery;
        });
      const addGallery = await Promise.all(newGallery);

      const newDocuments = productDocuments
        ?.filter(
          (c1) => !productDocumentsOriginal?.some((c2) => c1.id === c2.id),
        )
        .map(async (doc) => {
          const { fileName, file } = doc.file;
          return uploadToTempS3File(file, `document-upload/${fileName}`).then(
            (data) => {
              const newDoc = {
                document: data.id,
                name: doc.name,
              };

              return newDoc;
            },
          );
        });
      const addDocuments = await Promise.all(newDocuments);

      const removeDocuments = productDocumentsOriginal
        ?.filter((c1) => !productDocuments?.some((c2) => c1.id === c2.id))
        ?.map((c) => c.id);

      const addLinks = productLinks?.filter(
        (c1) => !productLinksOriginal?.some((c2) => c1.id === c2.id),
      );
      const removeLinks = productLinksOriginal
        ?.filter((c1) => !productLinks?.some((c2) => c1.id === c2.id))
        ?.map((c) => c.id);

      const newOptions = createFlatOptions(
        productOptions,
        productOptionsOriginal,
      );

      const addOptions = newOptions.add?.map((option) => {
        const newOption = { ...option };

        delete newOption.option_id;
        delete newOption.product_id;
        delete newOption.isVariant;

        return {
          ...newOption,
          // parent_id: undefined,
        };
      });

      const updateOptions = newOptions.update?.map((option) => {
        const newOption = { ...option };

        delete newOption.option_id;
        delete newOption.product_id;
        delete newOption.isVariant;

        return {
          ...newOption,
          // parent_id: undefined,
        };
      });

      const payload = {
        product,
        categories: { add: addCategories, remove: removeCategories },
        productGallery: { add: addGallery, remove: removeGallery },
        productDocuments: { add: addDocuments, remove: removeDocuments },
        productLinks: { add: addLinks, remove: removeLinks },
        productOptions: {
          add: addOptions,
          update: updateOptions,
          remove: newOptions.remove,
        },
      };

      updateProductApi(id, productId, removeUndefinedProperties(payload))
        .then(async () => {
          if (shouldNavigate) {
            navigate(-1);
          }
          Alert({
            icon: 'success',
            text: 'Product updated successfully',
          });

          const { data: updatedProductData } = await getCompanyProductApi(
            id,
            productId,
          );

          if (!updatedProductData?.data?.product?.template_id) {
            setTemplateData({});
          }

          setProductData(JSON.parse(JSON.stringify(updatedProductData)));

          updatedProductData.productOptions = recursiveSetOptionOrder(
            updatedProductData?.productOptions ?? [],
          );

          updatedProductData.categories =
            updatedProductData?.categories?.map((c) => c.category_id) ?? [];

          setLoading(false);
          onRefresh(updatedProductData);
        })
        .catch(() => {
          setLoading(false);
        });
    } catch (e) {
      setLoading(false);
    }
  };

  return (
    <ProductContextProvider value={contextValue}>
      <FormWrapper
        defaultValues={async () => {
          const { data } = await getCompanyProductApi(id, productId);

          if (data.product.template_id) {
            const template = await getCompanyTemplateApi(
              id,
              data.product.template_id,
            );

            if (template?.data) {
              const productDetails = convertTemplateToProduct(
                template,
                data.product.template_id,
              );

              setTemplateData(productDetails);
            }
          }

          setProductData(JSON.parse(JSON.stringify(data)));

          data.productOptions = recursiveSetOptionOrder(
            data?.productOptions ?? [],
          );

          data.categories = data?.categories?.map((c) => c.category_id) ?? [];
          return data;
        }}
        onSubmit={async (values) => {
          await onUpdateProduct(values);
        }}
      >
        <ProductForm
          type="edit"
          isLoading={editProductLoading || loading}
          onApply={(values, onRefresh) => {
            onUpdateProduct(values, false, onRefresh);
          }}
          templateData={templateData}
        />
      </FormWrapper>
    </ProductContextProvider>
  );
};

export default ProductEdit;
